/**
 * Yū — goal highlighting in the Shop panel.
 *
 * Hovering a goal in the left column emphasises that goal's products in the
 * A–Z list and dims the rest. Nothing moves and nothing is replaced.
 *
 * Why not swap the list for product cards: the Shop trigger sits directly
 * above the goal column (trigger x 170-269, goals x 40-260), so the pointer
 * lands on the goals on its way to the A–Z list. A swap would fire on every
 * entry into the panel, before the shopper has chosen anything. Dimming is
 * recoverable — an accidental hover costs nothing and undoes itself.
 *
 * Delegated: the theme re-renders the header after load.
 */
(function () {
  'use strict';

  var GOAL = '.yu-goal';
  var PANEL = '.yu-mega';
  var AZ = '.yu-mega__azcols';

  function clear(panel) {
    var list = panel.querySelector(AZ);
    if (list) {
      list.classList.remove('is-filtering');
      list.classList.remove('is-browsing');
    }

    var links = panel.querySelectorAll('.yu-mega__link');
    for (var i = 0; i < links.length; i++) links[i].classList.remove('is-match');

    var goals = panel.querySelectorAll(GOAL);
    for (var j = 0; j < goals.length; j++) goals[j].classList.remove('is-active');
  }

  function apply(panel, goal) {
    var handle = goal.getAttribute('data-goal');
    if (!handle) return;

    clear(panel);
    goal.classList.add('is-active');

    var list = panel.querySelector(AZ);
    if (!list) return;
    list.classList.add('is-filtering');

    var links = list.querySelectorAll('.yu-mega__link');
    for (var i = 0; i < links.length; i++) {
      var owned = (links[i].getAttribute('data-goals') || '').split(/\s+/);
      if (owned.indexOf(handle) !== -1) links[i].classList.add('is-match');
    }
  }

  function goalFrom(event) {
    var el = event.target;
    if (!el || typeof el.closest !== 'function') return null;
    return el.closest(GOAL);
  }

  /* Entering the A–Z list relaxes the filter to plain emphasis.

     Dimming to 0.3 is right while the shopper is scanning the categories —
     it answers "which of these are General Health?" at a glance. But it
     reads as *disabled*, and the moment the pointer crosses into the list
     the question has changed to "which one do I want?". Someone who hovers
     General Health and then spots TrimFit should be able to click TrimFit,
     not feel they have to go back and re-ask. So every product returns to
     full contrast and the match keeps its weight and colour. */
  function markBrowsing(el) {
    if (!el || typeof el.closest !== 'function') return;
    var list = el.closest(AZ);
    if (list && list.classList.contains('is-filtering')) list.classList.add('is-browsing');
  }

  document.addEventListener('mouseover', function (event) {
    var goal = goalFrom(event);
    if (goal) {
      var panel = goal.closest(PANEL);
      if (panel) apply(panel, goal);   /* apply() -> clear() drops is-browsing */
      return;
    }
    markBrowsing(event.target);
  });

  /* Keyboard users get the same emphasis when they tab onto a goal, and
     keep it as they tab on into the product list. */
  document.addEventListener('focusin', function (event) {
    var goal = goalFrom(event);
    if (goal) {
      var panel = goal.closest(PANEL);
      if (panel) apply(panel, goal);
      return;
    }
    markBrowsing(event.target);
  });

  /* ---------------------------------------------------------------
     The emphasis is dropped on leaving the whole panel, NOT on leaving
     the goal column.

     The column is only how you ask the question; the A–Z list is where you
     act on the answer. Clearing when the pointer crossed out of the column
     took the highlight away exactly as the shopper moved to click one of
     the products it had just pointed them at — by the time they arrived,
     everything was grey again.

     Hovering a different goal still switches cleanly, because apply()
     clears first.
     --------------------------------------------------------------- */
  function leftPanel(event) {
    var el = event.target;
    if (!el || typeof el.closest !== 'function') return null;
    var panel = el.closest(PANEL);
    if (!panel) return null;
    var to = event.relatedTarget;
    if (to && typeof to.closest === 'function' && to.closest(PANEL) === panel) return null;
    return panel;
  }

  document.addEventListener('mouseout', function (event) {
    var panel = leftPanel(event);
    if (panel) clear(panel);
  });

  document.addEventListener('focusout', function (event) {
    var panel = leftPanel(event);
    if (panel) clear(panel);
  });

  /* ---------------------------------------------------------------
     Click a goal -> the A–Z column becomes product cards for that goal.
     Click the same goal again, the back button, or Escape to return.

     Click rather than hover, deliberately: the Shop trigger sits directly
     over the goal column, so a hover swap would fire every time the panel
     opens. Click never fires by accident.
     --------------------------------------------------------------- */

  function showCards(panel, handle) {
    var az = panel.querySelector('.yu-mega__az');
    if (!az) return;

    var cards = az.querySelectorAll('[data-goal-cards]');
    var shown = null;
    for (var i = 0; i < cards.length; i++) {
      var match = cards[i].getAttribute('data-goal-cards') === handle;
      if (match) { cards[i].removeAttribute('hidden'); shown = cards[i]; }
      else cards[i].setAttribute('hidden', '');
    }
    if (!shown) return;

    az.classList.add('is-carded');
    panel.setAttribute('data-carded', handle);
    clear(panel);

    /* Drop any previous selection first. clear() only handles the hover
       class, so without this every category clicked stays highlighted. */
    var prev = panel.querySelectorAll('.yu-goal.is-selected');
    for (var p = 0; p < prev.length; p++) prev[p].classList.remove('is-selected');

    var goal = panel.querySelector('.yu-goal[data-goal="' + handle + '"]');
    if (goal) goal.classList.add('is-selected');

    /* Pull this panel's images now that it is on screen. */
    if (typeof window.yuNavUpgradeImages === 'function') window.yuNavUpgradeImages(shown);
  }

  function hideCards(panel) {
    var az = panel.querySelector('.yu-mega__az');
    if (!az) return;
    var cards = az.querySelectorAll('[data-goal-cards]');
    for (var i = 0; i < cards.length; i++) cards[i].setAttribute('hidden', '');
    az.classList.remove('is-carded');
    panel.removeAttribute('data-carded');
    var sel = panel.querySelectorAll('.yu-goal.is-selected');
    for (var j = 0; j < sel.length; j++) sel[j].classList.remove('is-selected');
  }

  document.addEventListener('click', function (event) {
    var el = event.target;
    if (!el || typeof el.closest !== 'function') return;

    /* Leave modified and non-primary clicks alone: the goal is a real <a>,
       so cmd/ctrl/shift-click and middle-click must still open the
       collection page in a new tab the way any link would. Without JS the
       link also just navigates, which is a sensible fallback. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof event.button === 'number' && event.button !== 0) return;

    if (el.closest('[data-goal-cards-back]')) {
      event.preventDefault();
      var p0 = el.closest(PANEL);
      if (p0) hideCards(p0);
      return;
    }

    var goal = el.closest(GOAL);
    if (!goal) return;
    var panel = goal.closest(PANEL);
    if (!panel) return;

    event.preventDefault();
    var handle = goal.getAttribute('data-goal');
    if (panel.getAttribute('data-carded') === handle) hideCards(panel);
    else showCards(panel, handle);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var panels = document.querySelectorAll(PANEL + '[data-carded]');
    for (var i = 0; i < panels.length; i++) hideCards(panels[i]);
  });
})();
